import { SyntheticEvent, useCallback, useEffect, useState } from 'react'
// @mui
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Unstable_Grid2'
import InputAdornment from '@mui/material/InputAdornment'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

// components
import Iconify from 'src/components/iconify'
import DateTimeRangePicker from 'src/components/date-time-range-picker'

import { IReportTableFilterValue, IReportTableFilters } from 'src/types/reports'
import useEvents from 'src/service/events.service'
import useUsers from 'src/service/users.service'
import useCategories from 'src/service/categories.service'
import useDebounce from 'src/hooks/use-debounce'
import { OptionType } from 'src/components/hook-form/rhf-autocomplete'
import { Autocomplete, Chip } from '@mui/material'

// ----------------------------------------------------------------------

type Props = {
  filters: IReportTableFilters
  onFilters: (name: string, value: IReportTableFilterValue) => void
  onExportExcel?: () => void
  onExportPDF?: () => void
  onRefresh?: () => void
  hasData?: boolean
  isLoading?: boolean
}

export default function ReportTableToolbar({
  filters,
  onFilters,
  onExportExcel,
  onExportPDF,
  onRefresh,
  hasData,
  isLoading: isRefreshing,
}: Props) {
  const [query, setQuery] = useState('')
  const searchDebounce = useDebounce(query, 500)

  // Terminal Serial com debounce
  const [terminalInput, setTerminalInput] = useState(filters.terminalSerial || '')
  const debouncedTerminal = useDebounce(terminalInput, 800)

  const { isLoading, isFetching, data } = useEvents(
    {
      page: 1,
      perPage: 100,
      sort: undefined,
    },
    {
      search: searchDebounce,
    },
  )

  useEffect(() => {
    if (debouncedTerminal !== filters.terminalSerial) {
      onFilters('terminalSerial', debouncedTerminal)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTerminal])

  useEffect(() => {
    if (filters.terminalSerial === '' && terminalInput !== '') {
      setTerminalInput('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.terminalSerial])

  const handleFilterDateRange = useCallback(
    (start: Date | null, end: Date | null) => {
      onFilters('dateStart', start)
      onFilters('dateEnd', end)
    },
    [onFilters],
  )

  const handleFilterService = useCallback(
    (event: SyntheticEvent<Element, Event>, selectedValue: OptionType | null) => {
      onFilters('event', selectedValue)
      // Categorias são vinculadas ao evento; trocar de evento invalida a seleção
      onFilters('categories', [])

      // Se um evento foi selecionado, definir automaticamente as datas do mês
      if (selectedValue?.value && data?.data) {
        const selectedEvent = data.data.find((evt) => evt._id === selectedValue.value)
        if (selectedEvent?.date) {
          const eventDate = new Date(selectedEvent.date)

          // Início do mês (dia 1, 00:00:00)
          const startOfMonth = new Date(eventDate.getFullYear(), eventDate.getMonth(), 1, 0, 0, 0)

          // Fim do mês (último dia, 23:59:59)
          const endOfMonth = new Date(
            eventDate.getFullYear(),
            eventDate.getMonth() + 1,
            0,
            23,
            59,
            59,
          )

          // Definir as datas automaticamente
          onFilters('dateStart', startOfMonth)
          onFilters('dateEnd', endOfMonth)
        }
      }
    },
    [onFilters, data],
  )

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={1.5} alignItems='flex-start'>
        {/* Período em destaque */}
        <Grid xs={12}>
          <DateTimeRangePicker
            label='Período'
            size='small'
            startDate={filters.dateStart}
            endDate={filters.dateEnd}
            onChange={handleFilterDateRange}
          />
        </Grid>

        {/* Evento */}
        <Grid xs={12} md={4}>
          <FormControl size='small' sx={{ width: '100%' }}>
            <Autocomplete
              id='event-list'
              size='small'
              options={
                data?.data?.map((option) => ({ label: option?.name, value: option?._id })) || []
              }
              isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
              getOptionLabel={(option: OptionType | string) => (option as OptionType)?.label}
              onChange={handleFilterService}
              value={filters.event}
              loading={isLoading || isFetching}
              inputValue={query}
              onInputChange={(event, newInputValue) => {
                setQuery(newInputValue)
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(params) => <TextField label='Evento' {...params} />}
            />
          </FormControl>
        </Grid>

        {/* Vendedores */}
        <Grid xs={12} md={3}>
          <SelectVendedores onFilters={onFilters} filters={filters} />
        </Grid>

        {/* Categorias */}
        <Grid xs={12} md={3}>
          <SelectCategorias onFilters={onFilters} filters={filters} />
        </Grid>

        {/* Terminal Serial */}
        <Grid xs={12} md={2}>
          <TextField
            label='Terminal'
            value={terminalInput}
            onChange={(e) => setTerminalInput(e.target.value)}
            placeholder='Serial'
            fullWidth
            size='small'
            helperText='Busca automática ao parar de digitar'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Iconify
                    icon='solar:smartphone-bold'
                    width={16}
                    sx={{ color: 'text.disabled' }}
                  />
                </InputAdornment>
              ),
            }}
            FormHelperTextProps={{
              sx: { mx: 0, fontSize: 11, color: 'text.secondary' },
            }}
          />
        </Grid>
      </Grid>

      {/* Faixa inferior: opções + botões de ação */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent='space-between'
        sx={{ width: 1, mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}
      >
        {/* Toggle: incluir produtos desativados */}
        <Tooltip
          title='Inclui produtos que foram desativados durante o evento (ex.: esgotados). Útil para gerar um relatório completo do evento.'
          arrow
          placement='top'
        >
          <FormControlLabel
            control={
              <Switch
                size='small'
                checked={!!filters.includeInactive}
                onChange={(e) => onFilters('includeInactive', e.target.checked)}
                color='warning'
              />
            }
            label={
              <Stack direction='row' alignItems='center' spacing={0.5}>
                <Iconify
                  icon='mdi:package-variant-closed-remove'
                  width={16}
                  sx={{ color: filters.includeInactive ? 'warning.main' : 'text.disabled' }}
                />
                <Typography
                  variant='caption'
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: filters.includeInactive ? 'warning.dark' : 'text.secondary',
                  }}
                >
                  Incluir produtos desativados
                </Typography>
                <Iconify
                  icon='solar:info-circle-bold'
                  width={13}
                  sx={{ color: 'text.disabled', opacity: 0.7 }}
                />
              </Stack>
            }
            sx={{ m: 0, gap: 0.5 }}
          />
        </Tooltip>

        {/* Botões */}
        {(onRefresh || onExportPDF || onExportExcel) && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.25}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
          {onRefresh && (
            <Button
              variant='outlined'
              size='small'
              startIcon={
                <Iconify
                  icon={isRefreshing ? 'eos-icons:loading' : 'eva:refresh-outline'}
                  width={16}
                />
              }
              onClick={onRefresh}
              disabled={isRefreshing}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 1.5,
                px: 2,
              }}
            >
              {isRefreshing ? 'Atualizando...' : 'Atualizar'}
            </Button>
          )}
          {onExportPDF && (
            <Button
              variant='outlined'
              size='small'
              startIcon={<Iconify icon='solar:document-bold' width={16} />}
              onClick={onExportPDF}
              disabled={!hasData}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                borderColor: '#B71C1C',
                color: '#B71C1C',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 1.5,
                px: 2,
                '&:hover': {
                  bgcolor: 'rgba(183, 28, 28, 0.08)',
                  borderColor: '#B71C1C',
                },
              }}
            >
              Exportar PDF
            </Button>
          )}
          {onExportExcel && (
            <Button
              variant='contained'
              size='small'
              startIcon={<Iconify icon='vscode-icons:file-type-excel' width={16} />}
              onClick={onExportExcel}
              disabled={!hasData}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                backgroundColor: '#1B5E20',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 1.5,
                px: 2,
                '&:hover': { backgroundColor: '#2E7D32' },
                '&:disabled': {
                  backgroundColor: 'rgba(145, 158, 171, 0.12)',
                  color: 'text.disabled',
                },
              }}
            >
              Exportar Excel
            </Button>
          )}
          </Stack>
        )}
      </Stack>
    </Box>
  )
}

// Componente para selecionar vendedores
export const SelectVendedores = ({ onFilters, filters }: { onFilters: any; filters: any }) => {
  const [query, setQuery] = useState('')
  const searchDebounce = useDebounce(query, 500)

  const { isLoading, isFetching, data } = useUsers(
    {
      page: 1,
      perPage: 100,
      sort: undefined,
    },
    {
      search: searchDebounce,
    },
  )

  const handleFilterService = useCallback(
    (event: SyntheticEvent<Element, Event>, selectedValues: any[]) => {
      onFilters('salers', selectedValues)
    },
    [onFilters],
  )

  return (
    <FormControl
      sx={{
        width: '100%',
      }}
    >
      <Autocomplete
        id='vendedores-list'
        multiple
        size='small'
        options={data?.data?.map((option) => ({ label: option?.name, value: option?._id })) || []}
        isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
        getOptionLabel={(option: OptionType | string) => (option as OptionType)?.label}
        onChange={handleFilterService}
        value={filters.salers}
        loading={isLoading || isFetching}
        inputValue={query}
        onInputChange={(event, newInputValue) => {
          setQuery(newInputValue)
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )}
        renderInput={(params) => <TextField label='Vendedores' {...params} />}
      />
    </FormControl>
  )
}

// Componente para selecionar categorias (depende do evento selecionado)
export const SelectCategorias = ({
  onFilters,
  filters,
}: {
  onFilters: any
  filters: IReportTableFilters
}) => {
  const [query, setQuery] = useState('')
  const searchDebounce = useDebounce(query, 500)

  const eventId = filters?.event?.value as string | undefined

  const { isLoading, isFetching, data } = useCategories(
    {
      page: 1,
      perPage: 100,
      sort: undefined,
    },
    {
      filter: searchDebounce,
      event: eventId,
    },
  )

  const handleFilterService = useCallback(
    (event: SyntheticEvent<Element, Event>, selectedValues: any[]) => {
      onFilters('categories', selectedValues)
    },
    [onFilters],
  )

  const disabled = !eventId

  return (
    <FormControl sx={{ width: '100%' }}>
      <Autocomplete
        id='categories-list'
        multiple
        size='small'
        disabled={disabled}
        options={
          data?.data?.map((option: any) => ({
            label: option?.name,
            value: option?._id,
          })) || []
        }
        isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
        getOptionLabel={(option: OptionType | string) => (option as OptionType)?.label}
        onChange={handleFilterService}
        value={filters.categories || []}
        loading={isLoading || isFetching}
        inputValue={query}
        onInputChange={(_event, newInputValue) => {
          setQuery(newInputValue)
        }}
        renderTags={(value: OptionType[], getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value as string}
              size='small'
              label={option.label}
            />
          ))
        }
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Categorias'
            placeholder={disabled ? 'Selecione um evento' : ''}
          />
        )}
      />
    </FormControl>
  )
}
